import React, { useEffect, useState } from "react";
import { Navigate, Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api, fmtPrice, BACKEND_URL } from "../lib/api";
import { User, ShoppingBag, Download, Pencil, LogOut, Check, Loader2, Mail, Phone, FileText, Trash2, KeyRound, Copy, MonitorOff, Clock3, Building2, Headphones, PackagePlus, RotateCcw, Ban, Archive, Webhook } from "lucide-react";
import { toast } from "sonner";

const marketplaceEnabled = process.env.REACT_APP_ENGINEER_MARKETPLACE_ENABLED === "true";

const licenseStatus = {
  active: ["Aktif", "bg-emerald-500/15 text-emerald-400"],
  unactivated: ["Belum diaktivasi", "bg-amber-500/15 text-amber-400"],
  pending_build: ["Sedang dibuat", "bg-sky-500/15 text-sky-300"],
  build_failed: ["Build gagal", "bg-red-500/15 text-red-400"],
  build_canceled: ["Build dibatalkan", "bg-zinc-500/15 text-zinc-400"],
  project_deleted: ["Project dihapus", "bg-zinc-500/15 text-zinc-400"],
  revoked: ["Dicabut", "bg-red-500/15 text-red-400"],
  expired: ["Kedaluwarsa", "bg-red-500/15 text-red-400"],
};

export default function CustomerDashboard() {
  const { user, loading, isCustomer, updateProfile, logout } = useAuth();
  const nav = useNavigate();
  const [orders, setOrders] = useState([]);
  const [licenses, setLicenses] = useState([]);
  const [distributions, setDistributions] = useState([]);
  const [distributionBusy, setDistributionBusy] = useState("");
  const [revealedApiKey, setRevealedApiKey] = useState("");
  const [issueForms, setIssueForms] = useState({});
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [saving, setSaving] = useState(false);
  const [savingMarketingPreference, setSavingMarketingPreference] = useState(false);

  useEffect(() => {
    if (isCustomer) {
      api.get("/customer/orders").then((r) => setOrders(r.data)).catch(() => setOrders([]));
      api.get("/customer/licenses").then((r) => setLicenses(r.data)).catch(() => setLicenses([]));
      api.get("/customer/license-distributions").then((r) => setDistributions(r.data)).catch(() => setDistributions([]));
      setForm({
        name: user.profile.name || "",
        email: user.profile.email || "",
        phone: user.profile.phone || "",
      });
    }
  }, [isCustomer, user]);

  if (loading) return <div className="pt-40 text-center text-zinc-500">Loading...</div>;
  if (!isCustomer) return <Navigate to="/login" state={{ from: "/dashboard" }} replace />;

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await updateProfile(form);
      toast.success("Profile updated");
      setEditing(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  const reloadLicenses = async () => {
    const r = await api.get("/customer/licenses");
    setLicenses(r.data);
  };

  const reloadDistributions = async () => {
    const r = await api.get("/customer/license-distributions");
    setDistributions(r.data);
  };


  const issueDistributionLicense = async (distributionId) => {
    const form = issueForms[distributionId] || {};
    if (!form.buyer_email) return;
    setDistributionBusy(`issue:${distributionId}`);
    try {
      const { data } = await api.post(`/customer/license-distributions/${distributionId}/licenses`, {
        buyer_name: form.buyer_name || "Customer",
        buyer_email: form.buyer_email,
        external_order_id: form.external_order_id || "",
        send_email: true,
      });
      setIssueForms((current) => ({ ...current, [distributionId]: {} }));
      await reloadDistributions();
      toast.success(data.email_sent ? "Serial dibuat dan dikirim" : "Serial dibuat; email belum terkirim");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Serial gagal dibuat");
    } finally {
      setDistributionBusy("");
    }
  };

  const rotateDistributionKey = async (distributionId) => {
    if (!window.confirm("API key lama akan langsung tidak berlaku. Lanjutkan?")) return;
    setDistributionBusy(`key:${distributionId}`);
    try {
      const { data } = await api.post(`/customer/license-distributions/${distributionId}/rotate-key`);
      setRevealedApiKey(data.api_key);
      await reloadDistributions();
      toast.success("API key baru dibuat");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "API key gagal diperbarui");
    } finally {
      setDistributionBusy("");
    }
  };

  const manageDistributionLicense = async (distributionId, licenseId, action) => {
    const labels = { reset: "reset seluruh perangkat", revoke: "mencabut serial", archive: "mengarsipkan serial", resend: "mengirim ulang email serial" };
    if (!window.confirm(`Yakin ingin ${labels[action]}?`)) return;
    setDistributionBusy(`${action}:${licenseId}`);
    try {
      if (action === "archive") await api.delete(`/customer/license-distributions/${distributionId}/licenses/${licenseId}`);
      else await api.post(`/customer/license-distributions/${distributionId}/licenses/${licenseId}/${action}`);
      await reloadDistributions();
      toast.success("Lisensi diperbarui");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Lisensi gagal diperbarui");
    } finally {
      setDistributionBusy("");
    }
  };

  const updateMarketingPreference = async () => {
    const nextConsent = !user.profile.marketing_consent;
    setSavingMarketingPreference(true);
    try {
      await updateProfile({ marketing_consent: nextConsent });
      toast.success(
        nextConsent
          ? "Email promosi telah diaktifkan"
          : "Email promosi telah dinonaktifkan",
      );
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Gagal memperbarui preferensi email");
    } finally {
      setSavingMarketingPreference(false);
    }
  };

  const deactivateDevice = async (licenseId, hardwareId) => {
    if (!window.confirm("Deactivate this computer? The plugin on that device will need activation again.")) return;
    try {
      await api.delete(`/customer/licenses/${licenseId}/devices/${encodeURIComponent(hardwareId)}`);
      await reloadLicenses();
      toast.success("Device deactivated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to deactivate device");
    }
  };

  const deleteFailedBuildLicense = async (licenseId) => {
    if (!window.confirm("Hapus lisensi build gagal ini dari Dashboard? Data ini tidak dapat dipulihkan.")) return;
    try {
      await api.delete(`/customer/licenses/${licenseId}`);
      setLicenses((current) => current.filter((license) => license.id !== licenseId));
      toast.success("Lisensi build gagal dihapus");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Lisensi gagal dihapus");
    }
  };

  const handleDownload = async (txnId, platform) => {
    try {
      const r = await api.get(`/download/${txnId}`, {
        params: { platform },
      });
      // Try opening the URL directly (works for Cloudinary / public URLs)
      const url = r.data.download_url;
      if (url && (url.startsWith("http://") || url.startsWith("https://"))) {
        window.open(url, "_blank", "noopener,noreferrer");
        toast.success("Download started");
      } else {
        toast.info(`File: ${r.data.filename}. Admin has not uploaded the file yet.`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Download failed");
    }
  };

  const downloadInvoice = async (txnId) => {
    try {
      const token = localStorage.getItem("ts_token");
      const res = await fetch(`${BACKEND_URL}/api/customer/invoice/${txnId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `invoice-${txnId.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Invoice downloaded");
    } catch {
      toast.error("Failed to fetch invoice");
    }
  };

  const paidOrders = orders.filter((o) => o.payment_status === "paid");
  const pendingOrders = orders.filter((o) => o.payment_status !== "paid");

  const deletePending = async (txnId) => {
    if (!window.confirm("Delete this pending order? This cannot be undone.")) return;
    try {
      await api.delete(`/customer/orders/${txnId}`);
      setOrders((prev) => prev.filter((o) => o.id !== txnId));
      toast.success("Order removed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete");
    }
  };


  return (
    <div data-testid="customer-dashboard" className="max-w-7xl mx-auto px-6 md:px-12 pt-28 pb-32">
      <div className="flex items-end justify-between mb-12 fade-up">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#e11d48] mb-3">My Account</div>
          <h1 className="font-[Outfit] text-4xl md:text-6xl font-black tracking-tighter">
            Hi, {user.profile.name?.split(" ")[0] || "there"}.
          </h1>
        </div>
        <div className="hidden md:flex items-center gap-2">
          {marketplaceEnabled && <Link to="/dashboard/services" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#e11d48] text-sm font-semibold hover:bg-[#be123c]"><Headphones className="w-3.5 h-3.5" /> Audio Projects</Link>}
          <button
            data-testid="dashboard-logout"
            onClick={() => { logout(); nav("/"); }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/15 text-sm hover:bg-white/5"
          >
            <LogOut className="w-3.5 h-3.5" /> Logout
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-[340px_1fr] gap-8">
        {/* Profile Card */}
        <aside className="bg-[#0a0a0c] border border-white/10 rounded-2xl p-6 h-fit">
          <div className="flex items-center justify-between mb-6">
            <div className="w-12 h-12 rounded-full bg-[#e11d48] flex items-center justify-center text-lg font-bold">
              {(user.profile.name || "U").slice(0, 1).toUpperCase()}
            </div>
            {!editing && (
              <button
                data-testid="edit-profile-btn"
                onClick={() => setEditing(true)}
                className="text-zinc-400 hover:text-white p-2 rounded hover:bg-white/5"
              >
                <Pencil className="w-4 h-4" />
              </button>
            )}
          </div>

          {!editing ? (
            <div className="space-y-4">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 mb-1">Name</div>
                <div data-testid="profile-name" className="font-semibold">{user.profile.name}</div>
              </div>
              {user.profile.email && (
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 mb-1">Email</div>
                  <div data-testid="profile-email" className="text-sm text-zinc-300 flex items-center gap-2">
                    <Mail className="w-3.5 h-3.5 text-zinc-500" />
                    {user.profile.email}
                  </div>
                </div>
              )}
              {user.profile.phone && (
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 mb-1">Phone</div>
                  <div data-testid="profile-phone" className="text-sm text-zinc-300 flex items-center gap-2">
                    <Phone className="w-3.5 h-3.5 text-zinc-500" />
                    {user.profile.phone}
                  </div>
                </div>
              )}
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 mb-1">Member since</div>
                <div className="text-sm text-zinc-300 font-mono">{(user.profile.created_at || "").slice(0, 10)}</div>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSave} className="space-y-4">
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 mb-1.5">Name</label>
                <input
                  data-testid="edit-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full bg-[#050505] border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#e11d48]"
                />
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 mb-1.5">Email</label>
                <input
                  data-testid="edit-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full bg-[#050505] border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#e11d48]"
                />
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 mb-1.5">Phone</label>
                <input
                  data-testid="edit-phone"
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="w-full bg-[#050505] border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#e11d48]"
                />
              </div>
              <div className="flex gap-2 pt-2">
                <button
                  data-testid="save-profile-btn"
                  type="submit"
                  disabled={saving}
                  className="flex-1 py-2.5 rounded-full bg-[#e11d48] hover:bg-[#be123c] text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Check className="w-3.5 h-3.5" /> Save</>}
                </button>
                <button
                  type="button"
                  onClick={() => setEditing(false)}
                  className="px-4 py-2.5 rounded-full border border-white/15 text-sm font-semibold hover:bg-white/5"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}

          <div className="mt-6 border-t border-white/10 pt-6">
            <div className="mb-3 flex items-start justify-between gap-4">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">Email Preferences</div>
                <p className="mt-2 text-sm font-semibold text-zinc-200">Product news and promotions</p>
                <p className="mt-1 text-xs leading-relaxed text-zinc-500">
                  Operational emails about your account, purchases, payments, and licenses are not affected.
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={!!user.profile.marketing_consent}
                data-testid="marketing-consent-toggle"
                onClick={updateMarketingPreference}
                disabled={savingMarketingPreference}
                className={`relative mt-1 h-6 w-11 shrink-0 rounded-full transition-colors disabled:opacity-50 ${user.profile.marketing_consent ? "bg-[#e11d48]" : "bg-zinc-700"}`}
              >
                <span className={`absolute left-1 top-1 h-4 w-4 rounded-full bg-white transition-transform ${user.profile.marketing_consent ? "translate-x-5" : "translate-x-0"}`} />
                <span className="sr-only">Toggle marketing emails</span>
              </button>
            </div>
            <p className={`text-xs font-medium ${user.profile.marketing_consent ? "text-emerald-400" : "text-zinc-500"}`}>
              {savingMarketingPreference
                ? "Saving preference..."
                : user.profile.marketing_consent
                  ? "Subscribed to marketing emails"
                  : "Not subscribed to marketing emails"}
            </p>
          </div>
        </aside>

        {/* Orders */}
        <section>
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-[Outfit] text-2xl font-bold">My Purchases</h2>
            <Link to="/shop" className="text-sm text-[#e11d48] hover:underline">Browse shop →</Link>
          </div>

          {orders.length === 0 && (
            <div className="bg-[#0a0a0c] border border-dashed border-white/10 rounded-2xl p-12 text-center">
              <ShoppingBag className="w-10 h-10 mx-auto text-zinc-600 mb-4" />
              <p className="text-zinc-400 mb-4">No purchases yet.</p>
              <Link to="/shop" className="inline-flex px-6 py-2.5 rounded-full bg-[#e11d48] hover:bg-[#be123c] text-sm font-semibold">
                Explore the Shop
              </Link>
            </div>
          )}

          {paidOrders.length > 0 && (
            <div className="space-y-3 mb-8">
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">Paid · {paidOrders.length}</div>
              {paidOrders.map((o) => (
                <div
                  key={o.id}
                  data-testid={`order-${o.id}`}
                  className="bg-[#0a0a0c] border border-white/10 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-4"
                >
                  {o.product_image && (
                    <img src={o.product_image} alt={o.product_name} className="w-16 h-16 rounded-lg object-cover flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate">{o.product_name}</div>
                    <div className="text-xs text-zinc-500 font-mono mt-1">
                      {(o.created_at || "").slice(0, 10)} · {fmtPrice(o.amount)}
                    </div>
                  </div>
                  <div className="w-full sm:w-auto flex flex-wrap justify-start sm:justify-end gap-2 sm:flex-shrink-0">
                    {o.product_asset_type !== 'vst_business_plan' && (o.available_platforms?.length
                      ? o.available_platforms
                      : ["windows"]
                    ).map((platform) => (
                      <button
                        key={platform}
                        data-testid={`download-${o.id}-${platform}`}
                        onClick={() => handleDownload(o.id, platform)}
                        className="inline-flex items-center justify-center gap-1.5 px-3 py-2 sm:px-4 sm:py-2.5 rounded-full bg-[#e11d48] hover:bg-[#be123c] text-xs sm:text-sm font-semibold whitespace-nowrap"
                      >
                        <Download className="w-3.5 h-3.5" />
                        {platform === "product"
                          ? "Download Product"
                          : platform === "macos"
                            ? "macOS"
                            : "Windows"}
                      </button>
                    ))}
                    <button
                      data-testid={`invoice-${o.id}`}
                      onClick={() => downloadInvoice(o.id)}
                      className="inline-flex items-center justify-center gap-1.5 px-3 py-2 sm:px-4 sm:py-2.5 rounded-full border border-white/15 hover:bg-white/5 text-xs sm:text-sm font-semibold whitespace-nowrap"
                      title="Download invoice PDF"
                    >
                      <FileText className="w-3.5 h-3.5" />
                      <span className="hidden sm:inline">Invoice</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {pendingOrders.length > 0 && (
            <div className="space-y-3">
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">Pending</div>
              {pendingOrders.map((o) => (
                <div
                  key={o.id}
                  data-testid={`pending-order-${o.id}`}
                  className="bg-[#0a0a0c] border border-white/5 rounded-xl p-4 flex items-center gap-4"
                >
                  {o.product_image && (
                    <img src={o.product_image} alt={o.product_name} className="w-16 h-16 rounded-lg object-cover flex-shrink-0 opacity-70" />
                  )}
                  <div className="flex-1 min-w-0 opacity-70">
                    <div className="font-semibold truncate">{o.product_name}</div>
                    <div className="text-xs text-zinc-500 font-mono mt-1">{fmtPrice(o.payable_amount || o.amount)}</div>
                  </div>
                  <span className="px-3 py-1 rounded-full text-[10px] font-semibold uppercase bg-amber-500/15 text-amber-400 flex-shrink-0">
                    {o.proof_status === "submitted" ? "verifying" : o.status || o.payment_status}
                  </span>
                  {o.payment_method === "manual_bank" && o.payment_status !== "failed" && (
                    <Link
                      to={`/payment/manual/${o.id}`}
                      title="Open bank transfer instructions"
                      className="p-2.5 rounded-full border border-[#e11d48]/30 bg-[#e11d48]/10 text-[#fb7185] hover:bg-[#e11d48]/20 transition-colors flex-shrink-0"
                    >
                      <Building2 className="w-3.5 h-3.5" />
                    </Link>
                  )}
                  {o.proof_status !== "submitted" && (
                    <button
                      data-testid={`delete-pending-${o.id}`}
                      onClick={() => deletePending(o.id)}
                      title="Remove this pending order"
                      className="p-2.5 rounded-full border border-white/10 hover:border-red-500/40 hover:bg-red-500/10 text-zinc-400 hover:text-red-400 transition-colors flex-shrink-0"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* My Licenses */}
          {licenses.length > 0 && (
            <div className="mt-10">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="font-[Outfit] text-2xl font-bold flex items-center gap-2">
                    <KeyRound className="w-5 h-5 text-[#e11d48]" />
                    My VST Licenses
                  </h2>
                  <p className="text-xs text-zinc-500 mt-0.5">Enter these keys in your plugin to activate.</p>
                </div>
              </div>
              <div className="space-y-3">
                {licenses.map((l) => {
                  const status = licenseStatus[l.status] || [l.status || "Tidak diketahui", "bg-zinc-500/15 text-zinc-400"];
                  const manageable = ["active", "unactivated"].includes(l.status);
                  const removableFailedBuild = l.source === "vst_maker_build" && ["build_failed", "build_canceled", "project_deleted"].includes(l.status);
                  return (
                  <div key={l.id} data-testid={`license-${l.id}`} className="bg-[#0a0a0c] border border-white/10 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="font-semibold">{l.product_name}</div>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase ${status[1]}`}>
                        {status[0]}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 bg-[#050505] border border-white/5 rounded-lg px-3 py-2">
                      <KeyRound className="w-3.5 h-3.5 text-[#e11d48] flex-shrink-0" />
                      <code className="flex-1 text-xs font-mono truncate">{manageable ? l.license_key : l.status === "pending_build" ? "Tersedia setelah build berhasil" : l.license_key}</code>
                      <button
                        data-testid={`copy-${l.id}`}
                        disabled={!manageable}
                        onClick={() => {
                          navigator.clipboard.writeText(l.license_key);
                          toast.success("License key copied");
                        }}
                        className="p-1.5 rounded hover:bg-white/5 text-zinc-400 hover:text-white flex-shrink-0 disabled:cursor-not-allowed disabled:opacity-25"
                        title="Copy to clipboard"
                      >
                        <Copy className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px] text-zinc-500">
                      <span className="rounded-full border border-white/10 px-2 py-1">
                        {l.license_type === "trial" ? `Trial · expires ${(l.expires_at || "").slice(0, 10)}` : "Lifetime license"}
                      </span>
                      {l.source === "vst_maker_build" && <span className="rounded-full border border-sky-400/20 bg-sky-400/5 px-2 py-1 text-sky-300">VST Maker</span>}
                      <span className="rounded-full border border-white/10 px-2 py-1">
                        {l.activation_count || (l.activations || []).length}/{l.max_activations || 1} computers
                      </span>
                      {l.license_type !== "trial" && <span className="rounded-full border border-white/10 px-2 py-1">Offline permanen setelah aktivasi</span>}
                    </div>
                    {(l.activations || []).length > 0 && (
                      <div className="mt-3 space-y-2">
                        {(l.activations || []).map((device) => (
                          <div key={device.hardware_id} className="flex items-center gap-2 rounded-lg border border-white/5 bg-[#050505] px-3 py-2">
                            <div className="flex-1 min-w-0">
                              <div className="text-xs font-medium truncate">{device.machine_name || "Activated computer"}</div>
                              <div className="text-[10px] text-zinc-600 font-mono truncate">{device.hardware_id}</div>
                            </div>
                            {manageable && <button
                              onClick={() => deactivateDevice(l.id, device.hardware_id)}
                              className="flex flex-col items-center p-2 text-zinc-400 hover:text-red-400"
                              title="Deactivate computer"
                            >
                              <MonitorOff className="w-3.5 h-3.5" />
                              <span className="mt-1 text-[9px] leading-none">Reset Device</span>
                            </button>}
                          </div>
                        ))}
                      </div>
                    )}
                    {removableFailedBuild && (
                      <div className="mt-3 flex items-center justify-between gap-3 rounded-lg border border-red-400/15 bg-red-400/[0.04] px-3 py-2">
                        <span className="text-[11px] leading-4 text-zinc-500">Build ini tidak menghasilkan lisensi yang dapat digunakan.</span>
                        <button
                          type="button"
                          onClick={() => deleteFailedBuildLicense(l.id)}
                          className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-red-400/20 px-3 py-2 text-[10px] font-bold text-red-300 hover:bg-red-400/10"
                        >
                          <Trash2 className="h-3.5 w-3.5" />Hapus
                        </button>
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>
            </div>
          )}

          {distributions.length > 0 && (
            <div className="mt-12" data-testid="distribution-licenses">
              <div className="mb-6">
                <h2 className="flex items-center gap-2 font-[Outfit] text-2xl font-bold"><PackagePlus className="h-5 w-5 text-[#e11d48]" />Lisensi Distribusi</h2>
                <p className="mt-1 text-xs text-zinc-500">Jual VST Anda dengan serial unik untuk setiap pembeli. Penerbitan dapat dilakukan manual atau otomatis melalui API.</p>
              </div>

              {revealedApiKey && (
                <div className="mt-4 rounded-2xl border border-amber-400/25 bg-amber-400/5 p-4">
                  <div className="text-xs font-bold text-amber-300">Simpan API key ini sekarang — hanya ditampilkan satu kali.</div>
                  <div className="mt-2 flex items-center gap-2"><code className="min-w-0 flex-1 overflow-hidden text-ellipsis rounded-lg bg-black px-3 py-2 text-xs">{revealedApiKey}</code><button type="button" onClick={() => { navigator.clipboard.writeText(revealedApiKey); toast.success("API key disalin"); }} className="rounded-lg border border-white/10 p-2"><Copy className="h-4 w-4" /></button></div>
                </div>
              )}

              <div className="mt-5 space-y-4">
                {distributions.map((distribution) => {
                  const form = issueForms[distribution.id] || {};
                  return (
                    <div key={distribution.id} className="rounded-2xl border border-white/10 bg-[#0a0a0c] p-5">
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div><div className="font-semibold">{distribution.product_name}</div><div className="mt-1 text-xs text-zinc-500">{distribution.max_activations} komputer per serial · API {distribution.api_key_prefix}…</div></div>
                        <div className="flex items-center gap-2"><span className="rounded-full border border-emerald-400/20 bg-emerald-400/5 px-3 py-1 text-xs font-bold text-emerald-300">{distribution.quota_remaining}/{distribution.quota_purchased} tersisa</span><button type="button" onClick={() => rotateDistributionKey(distribution.id)} disabled={distributionBusy === `key:${distribution.id}`} title="Buat ulang API key" className="rounded-lg border border-white/10 p-2 text-zinc-400 hover:text-white"><Webhook className="h-4 w-4" /></button></div>
                      </div>
                      <div className="mt-4 grid gap-2 md:grid-cols-[1fr_1fr_1fr_auto]">
                        <input value={form.buyer_name || ""} onChange={(event) => setIssueForms((current) => ({ ...current, [distribution.id]: { ...form, buyer_name: event.target.value } }))} placeholder="Nama pembeli" className="rounded-lg border border-white/10 bg-black px-3 py-2 text-xs" />
                        <input type="email" value={form.buyer_email || ""} onChange={(event) => setIssueForms((current) => ({ ...current, [distribution.id]: { ...form, buyer_email: event.target.value } }))} placeholder="Email pembeli" className="rounded-lg border border-white/10 bg-black px-3 py-2 text-xs" />
                        <input value={form.external_order_id || ""} onChange={(event) => setIssueForms((current) => ({ ...current, [distribution.id]: { ...form, external_order_id: event.target.value } }))} placeholder="Nomor order (opsional)" className="rounded-lg border border-white/10 bg-black px-3 py-2 text-xs" />
                        <button type="button" onClick={() => issueDistributionLicense(distribution.id)} disabled={!form.buyer_email || distribution.quota_remaining < 1 || distributionBusy === `issue:${distribution.id}`} className="inline-flex items-center justify-center gap-2 rounded-lg bg-white px-4 py-2 text-xs font-bold text-black disabled:opacity-40">{distributionBusy === `issue:${distribution.id}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <KeyRound className="h-3.5 w-3.5" />}Buat serial</button>
                      </div>
                      <div className="mt-4 rounded-xl border border-white/5 bg-black/40 px-3 py-2 text-[11px] text-zinc-500"><code>POST /api/license/distribution/issue</code> · Header <code>X-License-API-Key</code> · kirim <code>buyer_name</code>, <code>buyer_email</code>, dan <code>external_order_id</code>.</div>
                      {(distribution.licenses || []).filter((license) => !license.archived_at).length > 0 && <div className="mt-4 space-y-2">{(distribution.licenses || []).filter((license) => !license.archived_at).map((license) => {
                        const status = licenseStatus[license.status] || [license.status, "bg-zinc-500/15 text-zinc-400"];
                        return <div key={license.id} className="flex flex-wrap items-center gap-3 rounded-xl border border-white/5 bg-black/40 px-3 py-3"><div className="min-w-0 flex-1"><div className="truncate text-xs font-semibold">{license.customer_name} · {license.customer_email}</div><div className="mt-1 truncate font-mono text-[10px] text-zinc-500">{license.license_key} · {license.activation_count || 0}/{license.max_activations}</div></div><span className={`rounded-full px-2 py-0.5 text-[9px] font-bold uppercase ${status[1]}`}>{status[0]}</span><div className="flex items-center gap-1"><button type="button" onClick={() => { navigator.clipboard.writeText(license.license_key); toast.success("Serial disalin"); }} title="Salin serial" className="p-2 text-zinc-400 hover:text-white"><Copy className="h-3.5 w-3.5" /></button><button type="button" onClick={() => manageDistributionLicense(distribution.id, license.id, "resend")} title="Kirim ulang email" className="p-2 text-zinc-400 hover:text-emerald-300"><Mail className="h-3.5 w-3.5" /></button><button type="button" disabled={!['active', 'unactivated'].includes(license.status)} onClick={() => manageDistributionLicense(distribution.id, license.id, "reset")} title="Reset perangkat" className="p-2 text-zinc-400 hover:text-sky-300 disabled:opacity-25"><RotateCcw className="h-3.5 w-3.5" /></button><button type="button" onClick={() => manageDistributionLicense(distribution.id, license.id, "revoke")} title="Revoke" className="p-2 text-zinc-400 hover:text-amber-300"><Ban className="h-3.5 w-3.5" /></button><button type="button" onClick={() => manageDistributionLicense(distribution.id, license.id, "archive")} title="Arsipkan" className="p-2 text-zinc-400 hover:text-red-300"><Archive className="h-3.5 w-3.5" /></button></div></div>;
                      })}</div>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
